import { resolveProjectRouteScope } from '@/features/projects/utils/projectScopedRoutes';
import type { AuthSession } from '@/auth/types';

type OrganisationLike = { id: string; slug: string };
type ProjectLike = { id: string; slug: string; organisationId?: string | null };

type ResolutionSource = 'url' | 'persisted_user_context' | 'server_default' | 'unresolved';

export type ContextResolutionReasonCodes = {
  organisation: ResolutionSource;
  project: ResolutionSource;
};

export type ContextResolutionResult = {
  activeOrganisationId: string | null;
  activeProjectId: string | null;
  reasonCodes: ContextResolutionReasonCodes;
};

type ResolveActiveContextInput = {
  pathname: string;
  session: AuthSession | null;
  organisations: OrganisationLike[];
  projects: ProjectLike[];
  persistedOrganisationId: string | null;
  persistedProjectId: string | null;
};

const resolveOrgSlugFromPath = (pathname: string): string | null => {
  const projectScope = resolveProjectRouteScope(pathname);
  if (projectScope) return projectScope.workspaceSlug;
  const match = /^\/organisations\/([^/]+)/.exec(pathname);
  if (!match) return null;
  return decodeURIComponent(match[1]);
};

const resolveProjectSlugFromPath = (pathname: string): string | null => {
  const projectScope = resolveProjectRouteScope(pathname);
  if (projectScope) return projectScope.projectSlug;
  const match = /^\/projects\/([^/]+)/.exec(pathname);
  if (!match) return null;
  return decodeURIComponent(match[1]);
};

const matchesOrganisation = (
  organisation: OrganisationLike,
  organisationId: string | null,
): boolean => organisationId !== null && organisation.id === organisationId;

const findServerDefaultOrganisationId = (
  session: AuthSession | null,
  organisations: OrganisationLike[],
): string | null => {
  const serverOrganisationId =
    session?.lastOrganisationId ??
    session?.orgId ??
    (organisations.length > 0 ? (organisations[0]?.id ?? null) : null);
  if (serverOrganisationId === null) return null;
  const exists = organisations.some((organisation) =>
    matchesOrganisation(organisation, serverOrganisationId),
  );
  return exists ? serverOrganisationId : null;
};

const findServerDefaultProjectId = (
  resolvedOrganisationId: string | null,
  session: AuthSession | null,
  projects: ProjectLike[],
): string | null => {
  const candidates = projects.filter(
    (project) => (project.organisationId ?? null) === resolvedOrganisationId,
  );
  if (candidates.length === 0) return null;
  const preferredProjectId = session?.lastProjectId ?? null;
  if (preferredProjectId) {
    const preferredProject = candidates.find((project) => project.id === preferredProjectId);
    if (preferredProject) return preferredProject.id;
  }
  return candidates[0]?.id ?? null;
};

const resolveProjectFromUrl = (
  pathname: string,
  organisations: OrganisationLike[],
  projects: ProjectLike[],
): ProjectLike | null => {
  const projectSlug = resolveProjectSlugFromPath(pathname);
  if (!projectSlug) return null;
  const organisationSlug = resolveProjectRouteScope(pathname)?.workspaceSlug ?? null;
  const organisationId =
    organisationSlug === null
      ? null
      : (organisations.find((organisation) => organisation.slug === organisationSlug)?.id ?? null);
  if (organisationSlug !== null && organisationId === null) return null;
  return (
    projects.find((project) => {
      if (project.slug !== projectSlug) return false;
      if (organisationSlug === null) return true;
      return project.organisationId === organisationId;
    }) ?? null
  );
};

const resolveOrganisationFromUrl = (
  pathname: string,
  organisations: OrganisationLike[],
): OrganisationLike | null => {
  const organisationSlug = resolveOrgSlugFromPath(pathname);
  if (!organisationSlug) return null;
  return organisations.find((organisation) => organisation.slug === organisationSlug) ?? null;
};

export const resolveActiveContext = ({
  pathname,
  session,
  organisations,
  projects,
  persistedOrganisationId,
  persistedProjectId,
}: ResolveActiveContextInput): ContextResolutionResult => {
  const urlProject = resolveProjectFromUrl(pathname, organisations, projects);
  const urlOrganisation = resolveOrganisationFromUrl(pathname, organisations);

  const resolvedOrganisationIdFromUrl = urlProject
    ? (urlProject.organisationId ?? null)
    : (urlOrganisation?.id ?? null);

  let activeOrganisationId: string | null = null;
  let organisationSource: ResolutionSource = 'unresolved';

  if (resolvedOrganisationIdFromUrl !== null || urlOrganisation !== null || urlProject !== null) {
    activeOrganisationId = resolvedOrganisationIdFromUrl;
    organisationSource = 'url';
  } else if (
    persistedOrganisationId !== null &&
    organisations.some((organisation) => organisation.id === persistedOrganisationId)
  ) {
    activeOrganisationId = persistedOrganisationId;
    organisationSource = 'persisted_user_context';
  } else {
    activeOrganisationId = findServerDefaultOrganisationId(session, organisations);
    organisationSource = activeOrganisationId !== null ? 'server_default' : 'unresolved';
  }

  let activeProjectId: string | null = null;
  let projectSource: ResolutionSource = 'unresolved';

  if (urlProject) {
    activeProjectId = urlProject.id;
    projectSource = 'url';
  } else {
    const persistedProjectIsValid =
      persistedProjectId !== null &&
      projects.some(
        (project) =>
          project.id === persistedProjectId &&
          (project.organisationId ?? null) === activeOrganisationId,
      );
    if (persistedProjectIsValid) {
      activeProjectId = persistedProjectId;
      projectSource = 'persisted_user_context';
    } else {
      activeProjectId = findServerDefaultProjectId(activeOrganisationId, session, projects);
      projectSource = activeProjectId !== null ? 'server_default' : 'unresolved';
    }
  }

  return {
    activeOrganisationId,
    activeProjectId,
    reasonCodes: {
      organisation: organisationSource,
      project: projectSource,
    },
  };
};
